/**
 * Вход мини-приложения (Telegram и MAX - один бандл telegram-app): какой
 * заголовок с init data слать и что делать, когда сервер её больше не
 * принимает (401).
 *
 * Telegram и MAX подписывают init data разными ботами, поэтому заголовки
 * разные и вместе не уходят: сервер, получив оба, не пускает никого.
 * Среду выбираем как адаптер среды (redesign/platform): сначала Telegram,
 * потом MAX.
 */

export const TELEGRAM_INIT_DATA_HEADER = 'X-Telegram-Init-Data'
export const MAX_INIT_DATA_HEADER = 'X-Max-Init-Data'

/** Мост MAX: window.WebApp из https://st.max.ru/js/max-web-app.js */
interface MaxBridge {
  initData?: string | null
  close?(): void
}

interface TelegramBridge {
  initData?: string
  showAlert?(message: string, callback?: () => void): void
  close?(): void
}

type BridgeWindow = Window & {
  Telegram?: { WebApp?: TelegramBridge }
  WebApp?: MaxBridge
}

function telegramBridge(win: Window): TelegramBridge | undefined {
  const tg = (win as BridgeWindow).Telegram?.WebApp
  return tg?.initData ? tg : undefined
}

function maxBridge(win: Window): MaxBridge | undefined {
  const max = (win as BridgeWindow).WebApp
  return max?.initData ? max : undefined
}

/** Мини-приложение открыто в MAX (а не в Telegram и не в браузере). */
export function isMaxMiniApp(win: Window = window): boolean {
  return !telegramBridge(win) && !!maxBridge(win)
}

/** Заголовок с init data для запроса к API: не больше одного. */
export function miniAppAuthHeaders(win: Window = window): Record<string, string> {
  const tg = telegramBridge(win)
  if (tg?.initData) return { [TELEGRAM_INIT_DATA_HEADER]: tg.initData }
  const max = maxBridge(win)
  if (max?.initData) return { [MAX_INIT_DATA_HEADER]: max.initData }
  return {}
}

export const STALE_SESSION_TEXT = 'Сессия устарела. Откройте приложение заново.'

// Флаг не даёт показать сообщение на каждый упавший запрос.
let staleSessionHandled = false

/** Только для тестов: сбросить флаг «уже показали». */
export function resetStaleSessionForTests(): void {
  staleSessionHandled = false
}

/**
 * 401 в мини-приложении значит, что init data больше не принимается: чаще
 * всего приложение открыто дольше суток, а сервер не берёт init data старше
 * суток. Новую выдаёт только мессенджер при повторном открытии, поэтому один
 * раз говорим об этом.
 *
 * Telegram - системное окно showAlert и закрытие. В мосте MAX окна сообщений
 * нет (dev.max.ru/docs/webapps/bridge), поэтому - своё окно поверх приложения
 * с кнопкой «Закрыть» (WebApp.close есть в max-web-app.js). Вне мини-приложений
 * ничего не делает.
 */
export function handleMiniAppStaleSession(win: Window = window): void {
  if (staleSessionHandled) return

  const tg = telegramBridge(win)
  if (tg) {
    staleSessionHandled = true
    if (tg.showAlert) {
      tg.showAlert(STALE_SESSION_TEXT, () => tg.close?.())
    }
    return
  }

  const max = maxBridge(win)
  if (max) {
    staleSessionHandled = true
    showMaxStaleSessionNotice(win, max)
  }
}

function showMaxStaleSessionNotice(win: Window, max: MaxBridge): void {
  const doc = win.document
  const overlay = doc.createElement('div')
  overlay.setAttribute('role', 'alertdialog')
  overlay.setAttribute('aria-modal', 'true')
  overlay.setAttribute('aria-labelledby', 'pc-stale-session-text')
  overlay.dataset.testid = 'stale-session'
  // Системные цвета Canvas/CanvasText сами следуют светлой и тёмной теме.
  overlay.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:2147483647', 'display:flex',
    'align-items:center', 'justify-content:center', 'padding:16px',
    'background:rgba(0,0,0,0.45)',
  ].join(';')

  const box = doc.createElement('div')
  box.style.cssText = [
    'max-width:360px', 'width:100%', 'padding:20px', 'border-radius:16px',
    'background:Canvas', 'color:CanvasText', 'font:16px/1.4 system-ui,sans-serif',
    'text-align:center', 'box-shadow:0 8px 32px rgba(0,0,0,0.25)',
  ].join(';')

  const text = doc.createElement('p')
  text.id = 'pc-stale-session-text'
  text.style.cssText = 'margin:0 0 16px'
  text.textContent = STALE_SESSION_TEXT
  box.appendChild(text)

  if (typeof max.close === 'function') {
    const button = doc.createElement('button')
    button.type = 'button'
    button.textContent = 'Закрыть'
    button.style.cssText = [
      'min-height:44px', 'min-width:120px', 'padding:0 20px', 'border:0',
      'border-radius:12px', 'background:#2f6fed', 'color:#fff',
      'font:600 16px system-ui,sans-serif', 'cursor:pointer',
    ].join(';')
    button.addEventListener('click', () => {
      try {
        max.close?.()
      } catch {
        // Мост не закрыл - окно остаётся, текст уже объясняет, что делать.
      }
    })
    box.appendChild(button)
    overlay.appendChild(box)
    doc.body.appendChild(overlay)
    button.focus()
    return
  }

  overlay.appendChild(box)
  doc.body.appendChild(overlay)
}
